import { MoreVert } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { FieldListV2FormType } from '/@/components/field-list-v2';
import { useOwnUserNode } from '/@/hooks/nodes';
import {
    useHasWriteAccess,
    useIsValidatedBayerUserType,
    useIsValidatedConsultant,
    useIsValidatedRV,
} from '/@/hooks/session';
import { _ } from '/@/locale';
import { getCarbonCalculatorBREnabled, getCarbonCalculatorEnabled } from '/@/store/carbon-calculator/selectors';
import { getCornHeadToHeadEnabled, getSoyHeadToHeadEnabled } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import CreateRecommendationModal from '/@/views/dashboard/rv/components/create-recommendation-modal';
import CropMonitoringDialog from '/@/views/fields/field-monitoring/components/creation-dialog';
import AssignFieldModal from '../assign-field-modal';
import CreateVisitModal from '../create-visit-modal';
import DeleteFieldModal from '../delete-field-modal';
import FieldEditModal from '../edit-field-modal';

const FieldListV2MoreOptions = ({ field, form }: { field: Field; form: UseFormReturn<FieldListV2FormType> }) => {
    const navigate = useNavigate();
    const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<HTMLElement | null>(null);
    const [recommendationsModalOpened, setRecommendationsModalOpened] = useState(false);
    const [editFieldModalOpened, setEditFieldModalOpened] = useState(false);
    const [newCMModalOpened, setNewCMModalOpened] = useState(false);
    const [deleteFieldModalOpened, setDeleteFieldModalOpened] = useState(false);
    const [createVisitModalOpened, setCreateVisitModalOpened] = useState(false);
    const [assignToProducerModalOpened, setAssignToProducerModalOpened] = useState(false);
    const soyHeadToHeadEnabled = useSelector(getSoyHeadToHeadEnabled);
    const cornHeadToHeadEnabled = useSelector(getCornHeadToHeadEnabled);
    const carbonCalculatorEnabled = useSelector(getCarbonCalculatorEnabled);
    const { data: selfUserNode } = useOwnUserNode();
    const canEditResource = useHasWriteAccess();
    const isValidatedUserType = useIsValidatedBayerUserType();
    const isRv = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();
    const carbonCalculatorBREnabled = useSelector(getCarbonCalculatorBREnabled);
    const menuItems: {
        label: string;
        hidden?: boolean;
        onClick: (e: React.MouseEvent<HTMLElement>) => void;
        disabled?: boolean;
        dataTestId?: string;
    }[] = [
        {
            label: _('Generar recomendación'),
            hidden: !canEditResource,
            dataTestId: 'more-options-generate-recommendation',
            onClick: (e) => {
                // TODO: Fix this
                mpTrackEvent('RV recommendations pop up opened', {
                    entry_source: 'field-list',
                });
                mpTrackEvent('Productor home recommendations pop up opened', {
                    entry_source: 'field-list',
                });
                setRecommendationsModalOpened(true);
            },
        },
        {
            label: _('Crear informe de visita'),
            onClick: (e) => {
                mpTrackEvent('Create visit pop up opened', {
                    entry_source: 'field-list',
                });
                setCreateVisitModalOpened(true);
            },
            hidden: !isRv && !isConsultant,
        },
        {
            label: _('Simulador de rotación'),
            onClick: (e) => {
                if (carbonCalculatorBREnabled) {
                    navigate(reverse('CarbonCalculatorBR', { params: { fieldId: field.id } }));
                } else {
                    navigate(reverse('CarbonCalculator', { params: { fieldId: field.id } }));
                }
            },
            hidden: !canEditResource || (!carbonCalculatorEnabled && !carbonCalculatorBREnabled),
        },

        {
            label: _('H2H Maíz'),
            hidden: !cornHeadToHeadEnabled || !canEditResource,
            onClick: (e) => {
                navigate(
                    reverse('headToHead.HeadToHead', {
                        params: { fieldId: field.id, crop: 'corn' },
                    })
                );
                mpTrackEvent('H2H open', { entry_source: 'field-list' });
            },
        },
        {
            label: _('H2H Soja'),
            hidden: !soyHeadToHeadEnabled || !canEditResource,
            onClick: (e) => {
                navigate(
                    reverse('headToHead.HeadToHead', {
                        params: { fieldId: field.id, crop: 'soy' },
                    })
                );
                mpTrackEvent('H2H open', { entry_source: 'field-list' });
            },
        },
        {
            label: _('Agregar próximo cultivo'),
            onClick: (e) => {
                setNewCMModalOpened(true);
            },
            hidden: field.activeCropMonitoring || !canEditResource,
        },
        {
            label: _('Asignar a productor'),
            onClick: (e) => {
                mpTrackEvent('Field Assignment flow:Started', {
                    field_id: field.id,
                });
                setAssignToProducerModalOpened(true);
            },
            hidden: field.userNode !== selfUserNode?.id || !canEditResource || !isValidatedUserType,
        },
        {
            label: _('Ir al detalle del lote'),
            onClick: (e) => {
                navigate(reverse('fields.FieldDetail', { params: { fieldId: field.id } }));
            },
            dataTestId: 'more-options-go-to-field-detail',
        },
        {
            label: _('Editar lote'),
            onClick: (e) => {
                setEditFieldModalOpened(true);
            },
            hidden: !canEditResource,
            dataTestId: 'more-options-edit-field',
        },
        {
            label: _('Eliminar'),
            onClick: async (e) => {
                mpTrackEvent('Field deleted', undefined);
                setDeleteFieldModalOpened(true);
            },
            hidden: !canEditResource,
            dataTestId: 'more-options-delete-field',
        },
    ].filter((item) => !item.hidden);

    return (
        <Box>
            <IconButton
                size='small'
                onClick={(e) => {
                    e.stopPropagation();
                    setRowActionsAnchorEl(e.currentTarget);
                }}
                color='primary'
                data-testid='field-list-v2-more-options-button'
            >
                <MoreVert />
            </IconButton>
            <Menu
                open={!!rowActionsAnchorEl}
                onClose={(e) => {
                    e.stopPropagation();
                    setRowActionsAnchorEl(null);
                }}
                anchorEl={rowActionsAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {menuItems.map((item, index) => (
                    <MenuItem
                        key={index}
                        onClick={(e) => {
                            e.stopPropagation();
                            item.onClick(e);
                            setRowActionsAnchorEl(null);
                        }}
                        disabled={item.disabled}
                        data-testid={item.dataTestId}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Menu>
            <CreateRecommendationModal
                open={recommendationsModalOpened}
                setOpen={() => setRecommendationsModalOpened(!recommendationsModalOpened)}
                selectedField={field || undefined}
                setSelectedField={(field) => {}}
                userNodeId={field.userNode}
                entrySource='field-list'
            />
            <FieldEditModal open={editFieldModalOpened} onClose={() => setEditFieldModalOpened(false)} field={field} />
            <CropMonitoringDialog
                open={newCMModalOpened}
                setClose={() => setNewCMModalOpened(false)}
                field={field}
                entry_source='field-list'
            />
            {createVisitModalOpened && (
                <CreateVisitModal
                    open={createVisitModalOpened}
                    setOpen={() => setCreateVisitModalOpened(false)}
                    field={field}
                    userNodeId={field.userNode}
                />
            )}
            <DeleteFieldModal
                fieldId={field.id}
                onSuccess={() => {
                    form.setValue('fieldId', null);
                }}
                open={deleteFieldModalOpened}
                setOpen={setDeleteFieldModalOpened}
            />
            {!(field.userNode !== selfUserNode?.id) && (
                <AssignFieldModal
                    open={assignToProducerModalOpened}
                    setOpen={setAssignToProducerModalOpened}
                    field={field}
                />
            )}
        </Box>
    );
};

export default FieldListV2MoreOptions;
